import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import get from 'lodash/get';
import { PRODUCT_MODELS } from '../util/constants';
import { INSIGHTS_TAB, REPORTS_TAB } from './lib/constants';
import MLTabPanel from '../../components/MLTabPanel';
import {
  parseUrlQuery,
  isQQInsightsSupported,
  toQueryString,
} from '../util/util';
import { getConfigObject } from '../util/config';
import {
  getCourseAbstract,
  setCurrentNavigationPath,
  clearNavigationNodeState,
} from '../actions/reports';
import { clearInsightModals } from '../actions/insights';

import { withLocation, withNavigate, withParams } from '../../hoc';
import style from './ReportsTabs.module.css';

import QuantQualInsights from './components/QuantQualInsights';
import InsightsTabs from './components/InsightsTabs';

export class ReportsTabs extends Component {
  componentDidUpdate(prevProps) {
    const { location, clearInsightModals } = this.props;

    // Clear modals when route changes (browser back/forward or programmatic navigation)
    if (location && prevProps.location) {
      const routeChanged =
        location.pathname !== prevProps.location.pathname ||
        location.search !== prevProps.location.search;

      if (routeChanged && clearInsightModals) {
        clearInsightModals();
      }
    }
  }

  handleSubTabChange = (activeReportTab) => {
    const { onTabChange, clearInsightModals } = this.props;

    // Clear navigation state first
    this.props.clearNavigationNodeState();

    // Clear modals when switching tabs
    if (clearInsightModals) {
      clearInsightModals();
    }

    // Use the parent's onTabChange to handle URL updates
    const tabSearch = toQueryString({ activeTab: activeReportTab });

    if (onTabChange) {
      onTabChange('reports', tabSearch);
    }
  };

  isInsightsSupportedProductModel() {
    const {
      reports: { courseAbstract },
    } = this.props;

    const skillsModelEnabled = getConfigObject()
      .DASHBOARDS_ENABLED_BOSTON_INSIGHTS;
    return (
      courseAbstract &&
      courseAbstract.productModelId &&
      (isQQInsightsSupported(courseAbstract.productModelId) ||
        (courseAbstract.productModelId === PRODUCT_MODELS.skills &&
          skillsModelEnabled))
    );
  }

  isSkills() {
    const {
      reports: { courseAbstract },
    } = this.props;
    return (
      courseAbstract &&
      courseAbstract.productModelId &&
      courseAbstract.productModelId === PRODUCT_MODELS.skills
    );
  }

  isBaseReportsRoute() {
    const pathname = get(this, 'props.location.pathname', '');
    const { courseId } = this.props.params;
    const baseReportsPath = `/courses/${courseId}/reports`;

    return (
      pathname === baseReportsPath || pathname.startsWith(`${baseReportsPath}?`)
    );
  }

  getReportsTabContent = () => {
    const isBaseRoute = this.isBaseReportsRoute();

    if (!isBaseRoute && this.props.reportToShow) {
      return this.props.reportToShow;
    }

    return this.props.children;
  };

  render() {
    // Use window.location.search to get the current URL state, especially important for browser navigation
    const currentSearch = window.location.search;
    const { activeTab = REPORTS_TAB } = parseUrlQuery(currentSearch);
    const pathname = get(this, 'props.location.pathname', '');
    const TAB_LIST = [REPORTS_TAB, INSIGHTS_TAB];
    const { FF_REPORTS_V2 = false } = getConfigObject();

    if (!TAB_LIST.includes(activeTab)) {
      if (
        this.props.onTabChange &&
        typeof this.props.onTabChange === 'function'
      ) {
        this.props.onTabChange(REPORTS_TAB, `activeTab=${INSIGHTS_TAB}`);
      }
    }

    if (!activeTab || !pathname.includes('/reports')) return null;

    const {
      match,
      reports: { courseAbstract },
    } = this.props;

    return (
      <div className={style.tabContainer}>
        <MLTabPanel
          key={`tab-panel-${activeTab}`}
          ariaLabel="Reports"
          activeTab={activeTab}
          tabClassName={style.tab}
          onTabClick={this.handleSubTabChange}
        >
          {this.isInsightsSupportedProductModel() && (
            <div className={style.reportTab} label={INSIGHTS_TAB}>
              {FF_REPORTS_V2 ? (
                <InsightsTabs
                  match={match}
                  onTabChange={this.props.onTabChange}
                  courseAbstract={courseAbstract}
                />
              ) : (
                <QuantQualInsights
                  match={match}
                  onTabChange={this.props.onTabChange}
                  courseAbstract={courseAbstract}
                />
              )}
            </div>
          )}
          <div className={style.reportTab} label={REPORTS_TAB}>
            {this.getReportsTabContent()}
          </div>
        </MLTabPanel>
      </div>
    );
  }
}

ReportsTabs.propTypes = {
  onTabChange: PropTypes.func,
  reports: PropTypes.object,
  setCurrentNavigationPath: PropTypes.func,
  getCourseAbstract: PropTypes.func,
  clearNavigationNodeState: PropTypes.func,
  clearInsightModals: PropTypes.func,
  match: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  reportToShow: PropTypes.node,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  course: PropTypes.object,
  params: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    course: state.courses.course,
    reports: state.reports,
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setCurrentNavigationPath,
      getCourseAbstract,
      clearNavigationNodeState,
      clearInsightModals,
    },
    dispatch,
  );

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withNavigate,
  withLocation,
  withParams,
)(ReportsTabs);
